import { Blob } from '@google/genai';

const B64_CHUNK = 0x8000;

/**
 * Base64-encode PCM bytes using chunked String.fromCharCode + btoa (avoids huge spreads / string concat per byte).
 */
function pcmBytesToBase64(bytes: Uint8Array): string {
  let binary = '';
  for (let i = 0; i < bytes.length; i += B64_CHUNK) {
    binary += String.fromCharCode(...bytes.subarray(i, i + B64_CHUNK));
  }
  return btoa(binary);
}

/**
 * Converts a base64 string to a Uint8Array.
 */
export function decode(base64: string): Uint8Array {
  const binaryString = atob(base64);
  const len = binaryString.length;
  const bytes = new Uint8Array(len);
  for (let i = 0; i < len; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }
  return bytes;
}

/**
 * Converts a Uint8Array to a base64 string (chunked).
 */
export function encode(bytes: Uint8Array): string {
  return pcmBytesToBase64(bytes);
}

/**
 * Decodes raw little-endian PCM16 into an AudioBuffer.
 * Safe when `data` is a subarray (honors byteOffset / byteLength).
 *
 * Optimized hot path: mono @ 24000 Hz matches Gemini Live output and TeacherLive's
 * output AudioContext(sampleRate: 24000) — no resampling in that setup.
 */
export async function decodeAudioData(
  data: Uint8Array,
  ctx: AudioContext,
  sampleRate: number,
  numChannels: number,
): Promise<AudioBuffer> {
  const bytes = data.byteLength;
  const frameCount = Math.floor(bytes / (2 * numChannels));
  if (frameCount <= 0) {
    return ctx.createBuffer(numChannels, 1, sampleRate);
  }

  const buffer = ctx.createBuffer(numChannels, frameCount, sampleRate);
  const view = new DataView(data.buffer, data.byteOffset, data.byteLength);

  if (numChannels === 1) {
    const channelData = buffer.getChannelData(0);
    for (let i = 0; i < frameCount; i++) {
      channelData[i] = view.getInt16(i * 2, true) / 32768.0;
    }
    return buffer;
  }

  for (let channel = 0; channel < numChannels; channel++) {
    const channelData = buffer.getChannelData(channel);
    for (let i = 0; i < frameCount; i++) {
      channelData[i] = view.getInt16((i * numChannels + channel) * 2, true) / 32768.0;
    }
  }
  return buffer;
}

/**
 * Creates a Gemini-compatible Blob from float32 mic samples.
 * Float32 → little-endian PCM16 via DataView; base64 via chunked btoa.
 */
export function createBlob(data: Float32Array): Blob {
  const l = data.length;
  const out = new ArrayBuffer(l * 2);
  const view = new DataView(out);
  for (let i = 0; i < l; i++) {
    const s = Math.max(-1, Math.min(1, data[i]));
    const v = s < 0 ? Math.round(s * 0x8000) : Math.round(s * 0x7fff);
    view.setInt16(i * 2, v, true);
  }
  return {
    data: pcmBytesToBase64(new Uint8Array(out)),
    mimeType: 'audio/pcm;rate=16000',
  };
}
